import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaBuilding, FaUserTie, FaLayerGroup, FaClock, FaArrowLeft, FaEnvelopeOpenText, FaPlus, FaTimes, FaHistory } from 'react-icons/fa'; 
import api from '../../../src/api/axios'; 
import { ROLES, NOT_REQUESTABLE_ROLES, roleKey } from '../../constants/roles';
import './RoleSelection.css';

const RoleSelection = () => {
  const navigate = useNavigate();

  const [apiData, setApiData] = useState({ companies: [], roles: [], existing_roles: {}, blocked_combos: [] });
  const [selectedRequests, setSelectedRequests] = useState([{ company_id: "", role_id: "" }]);
  const [pendingRequests, setPendingRequests] = useState([]);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get("/api/companies-with-roles");
        const fetchedData = response.data.data || response.data;
        // Admin and Superadmin are granted by a Superadmin only, never requested (the API drops them
        // too; this keeps them out of the list even if it ever returned them).
        const notRequestableKeys = NOT_REQUESTABLE_ROLES.map(roleKey);
        fetchedData.roles = (fetchedData.roles || []).filter((r) => !notRequestableKeys.includes(roleKey(r.name)));
        setApiData(fetchedData);

        // Fetch pending requests
        const requestsResponse = await api.get("/api/my-role-requests");
        const pending = requestsResponse.data.filter(r => r.status === 'pending');
        setPendingRequests(pending);

        // Pre-fill Greenland (ID 2) by default
        const greenland = fetchedData.companies.find(c => c.name.toLowerCase().includes('greenland') || c.id === 2);
        if (greenland) {
          setSelectedRequests([{ company_id: String(greenland.id), role_id: "" }]);
        }
     
        setIsLoading(false);
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to load data from server.");
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const addRequestRow = () => {
    const defaultCompanyId = selectedRequests[0]?.company_id || "2";
    setSelectedRequests([...selectedRequests, { company_id: defaultCompanyId, role_id: "" }]);
  };

  const removeRequestRow = (index) => {
    if (selectedRequests.length > 1) {
      setSelectedRequests(selectedRequests.filter((_, i) => i !== index));
    }
  };

  const updateRequest = (index, field, value) => {
    const updated = [...selectedRequests];
    updated[index][field] = value;
    // Reset role if company changes
    if (field === 'company_id') {
      updated[index].role_id = "";
    }
    setSelectedRequests(updated);
  };

  const getExistingRole = (companyId) => {
    if (!apiData.existing_roles || !apiData.existing_roles[companyId]) {
      return null;
    }
    const roleIds = apiData.existing_roles[companyId];
    if (roleIds && roleIds.length > 0) {
      return apiData.roles.find(r => r.id === roleIds[0]);
    }
    return null;
  };

  const hasPendingRequest = (companyId, roleId) => {
    return pendingRequests.some(
      r => r.company === apiData.companies.find(c => c.id === companyId)?.name &&
           r.role === apiData.roles.find(r => r.id === roleId)?.name &&
           r.status === 'pending'
    );
  };

  const isBlocked = (companyId, roleId) => {
    if (!companyId || !roleId) return false;
    const combos = apiData.blocked_combos || [];
    return combos.some(
      c => String(c.company_id) === String(companyId) && String(c.role_id) === String(roleId)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Filter out empty requests
    let validRequests = selectedRequests.filter(
      r => r.company_id && r.role_id
    );

    if (validRequests.length === 0) {
      setError("Please select at least one company and role.");
      return;
    }

    // Filter out blocked combinations (edge case: stale data, race conditions)
    validRequests = validRequests.filter(
      r => !isBlocked(r.company_id, r.role_id)
    );

    if (validRequests.length === 0) {
      setError("Selected company and role combination(s) are not available for application.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await api.post("/api/role-apply", {
        requests: validRequests.map(r => ({
          company_id: parseInt(r.company_id),
          role_id: parseInt(r.role_id),
        })),
      });

      setIsSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || "Submission failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getLabel = (list, val, type) => {
    if (type === 'company') {
      const item = list.find(i => String(i.id) === String(val));
      return item?.name || "Unknown";
    }
    if (type === 'role') {
      const item = list.find(i => String(i.id) === String(val));
      return item?.name || "Unknown";
    }
    return val; 
  };

  const formatRoleName = (name) => {
    // Role names come from the roles table (canonical names); only GM needs a friendlier label.
    const mapping = {
      [ROLES.GM]: 'General Manager',
    };
    return mapping[name] || name;
  };

  if (isLoading) return <div className="rs-loading-spinner">Loading Workspace...</div>;

  return (
    <div className="rs-main-container">
      <div className="rs-content-card">
        {!isSubmitted ? (
          <>
            <div className="rs-header-section">
              <div className="rs-icon-box">
                <FaLayerGroup />
              </div>
              <h2 className="rs-title">Apply for a Role</h2>
              <p className="rs-subtitle">Select your organization(s) and role(s) to request access. You can apply for multiple companies at once.</p>
            </div>

            {pendingRequests.length > 0 && (
              <div className="rs-pending-banner">
                <FaHistory />
                <span>You have {pendingRequests.length} pending request(s). </span>
                <Link to="/role-requests" className="rs-link">View History</Link>
              </div>
            )}

            {error && <div className="rs-alert-error">{error}</div>}

            <form onSubmit={handleSubmit} className="rs-form-wrapper">
              {selectedRequests.map((request, index) => {
                const existingRole = getExistingRole(request.company_id);
                const isPending = request.company_id && request.role_id 
                  ? hasPendingRequest(request.company_id, request.role_id)
                  : false;

                return (
                  <div key={index} className="rs-request-row">
                    <div className="rs-request-header">
                      <h3>Request #{index + 1}</h3>
                      {selectedRequests.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRequestRow(index)}
                          className="rs-remove-btn"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>

                    {existingRole && (
                      <div className="rs-existing-role-banner">
                        Current role: <strong>{formatRoleName(existingRole.name)}</strong> 
                        <span className="rs-update-note">(This will update your role)</span>
                      </div>
                    )}

                    {isPending && (
                      <div className="rs-pending-warning">
                        You already have a pending request for this company and role.
                      </div>
                    )}



                    <div className="rs-field-group">
                      <label className="rs-field-label">Desired Role</label>
                      <div className="rs-select-box-container">
                        <FaUserTie className="rs-select-icon" />
                        <select 
                          className="rs-native-select"
                          value={request.role_id} 
                          onChange={(e) => updateRequest(index, 'role_id', e.target.value)}
                          required
                          disabled={!request.company_id}
                        >
                          <option value="" disabled>Choose Role</option>
                          {apiData.roles.map((r) => (
                            <option
                              key={r.id}
                              value={r.id}
                              disabled={isBlocked(request.company_id, r.id)}
                            >
                              {formatRoleName(r.name)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={addRequestRow}
                className="rs-add-request-btn"
              >
                <FaPlus /> Add Another Request
              </button>

              <button 
                type="submit" 
                className={`rs-submit-action-btn ${isSubmitting ? 'rs-btn-loading' : ''}`}
                disabled={isSubmitting || selectedRequests.every(r => !r.company_id || !r.role_id)}
              >
                {isSubmitting ? "Submitting..." : "Submit for Approval"}
              </button>
            </form>

            <div className="rs-footer-links">
              <Link to="/role-requests" className="rs-link">
                <FaHistory /> View Request History
              </Link>
            </div>
          </>
        ) : (
          <div className="rs-success-content">
            <div className="rs-status-badge-wrapper">
              <FaClock className="rs-clock-animation" />
            </div>
            <h2 className="rs-title">Request(s) Submitted</h2>
            <p className="rs-subtitle">Your request(s) will be reviewed by the approval authority.</p>
            
            <div className="rs-notification-banner">
              <FaEnvelopeOpenText />
              <p>We'll notify you via email once approved.</p>
            </div>

            <div className="rs-action-buttons">
              <button className="rs-navigation-back-btn" onClick={() => navigate(-1)}>
                <FaArrowLeft /> Go Back
              </button>
              <Link to="/role-requests" className="rs-view-history-btn">
                <FaHistory /> View Request History
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleSelection;
